<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :nudge-right="40"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template v-slot:activator="{ on, attrs }">
      <ValidationProvider
        :mode="validationModeComputed"
        v-slot="{ errors }"
        :rules="rulesComputed"
        :name="labelComputed"
      >
        <v-text-field
          ref="self"
          v-model="model"
          v-on="on"
          v-bind="attrs"
          class="text-field-date"
          :label="fullLabel"
          :error-messages="errors"
          :hint="hint"
          :persistent-hint="true"
          :disabled="disabled"
          :clearable="clearable"
          dense
          outlined
          readonly
          hide-details="auto"
          append-icon="mdi-calendar"
          @blur="onBlur"
          @change="onChange"
          @click:append="menu = true"
          @click:clear="model = null"
          @input="onInput"
        />
      </ValidationProvider>
    </template>
    <v-date-picker
      ref="picker"
      v-model="model"
      :type="typeComputed"
      @input="menu = false"
      @click:year="saveYear()"
      no-title
      reactive
      :active-picker="activePickerComputed"
      :min="minDateComputed"
      :max="maxDateComputed"
    />
  </v-menu>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import moment from 'moment'

export default {
  name: 'TextFieldInputWithDatePicker',

  components: {
    ValidationProvider,
    moment,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: 'date',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
    },
    rules: {
      type: String,
      required: false,
      default: null,
    },
    validationMode: {
      type: String,
      required: false,
      default: 'aggressive',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    disablePastDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableFutureDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    minDate: {
      // disablePastDateと同時に渡された場合、disablePastDateの制約が優先
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      menu: false,
      activePicker: '',
    }
  },

  computed: {
    fullLabel() {
      return this.required ? `${this.label} ${this.$t('ELE0024')}` : this.label
    },
    suffixComputed() {
      return this.suffix
    },
    labelComputed() {
      return this.label
    },
    rulesComputed() {
      if (this.rules && this.required) {
        return `required|${this.rules}`
      } else if (this.required) {
        return 'required'
      } else if (this.rules) {
        return this.rules
      } else {
        return ''
      }
    },
    validationModeComputed() {
      return this.validationMode
    },
    model: {
      get() {
        return this.type === 'year' ? this.value.slice(0, 4) : this.value
      },
      set(value) {
        if (value === '') {
          this.initTableDate()
        }
        this.$emit('input', value)
      },
    },
    typeComputed() {
      return this.type === 'year' ? 'month' : this.type
    },
    activePickerComputed: {
      get() {
        return this.activePicker
      },
      set(value) {
        this.activePicker = value
      },
    },
    minDateComputed: {
      get() {
        return this.disablePastDate ? moment().format('YYYY-MM-DD') : this.minDate
      },
    },
    maxDateComputed: {
      get() {
        return this.disableFutureDate ? moment().format('YYYY-MM-DD') : ''
      },
    },
  },

  methods: {
    onBlur() {
      this.$emit('blur')
    },
    onChange() {
      this.$emit('change')
    },
    onInput() {
      this.$emit('input')
    },
    saveYear() {
      if (this.type === 'year') {
        this.model = this.$refs.picker.tableDate
        this.menu = false
      }
    },
    initTableDate() {
      const today = new Date()
      this.$refs.picker.tableDate = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2)
    },
  },

  watch: {
    menu(value) {
      if (value) {
        /* TextBox末尾のiconクリック後、TextBoxへFocusをあてる処理.
        iconクリック -> カレンダーポップアップ表示 -> ポップアップ外をクリックしてCloseした場合も
        TextBoxバリデーションを走らせるための実装. */
        this.$refs.self.focus()
      }
    },
  },

  mounted() {},
}
</script>
<style>
/* 背景色・ボーダー色指定 */
.text-field-date > .v-input__control > .v-input__slot > fieldset {
  background: #edeffd;
  /* margin-bottom: 2px; */
  border-style: solid;
  border-width: 2px;
  border-color: var(--v-primary-base);
}

/* フォーカスが外れている場合のラベル色 */
.text-field-date > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  color: var(--v-primary-base);
}

.text-field-date > .v-input__prepend-outer > .v-input__icon > .v-icon {
  color: var(--v-primary-base);
}

/** エラーメッセージの上端が欠けるのを回避 */
.text-field-date > .v-input__control .v-messages {
  margin-top: 1px;
}
/* 非活性の選択値のボーダー色 */
.text-field-date.v-input--is-disabled > .v-input__control > .v-input__slot > fieldset {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.26);
  border-width: 1px;
  color: #000000;
}
/* 非活性のラベル色 */
.text-field-date.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  color: #000000 !important;
}
/* 非活性の選択値のテキスト色 */
.text-field-date.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: #000000 !important;
}
</style>
